import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { useParams } from 'react-router-dom';

import { FontAwesomeIcon, Section } from '@appcharge/shared-ui';
import useFeatureFlags from 'api/useFeatureFlags';
import useOffers from 'api/useOffers';
import usePricing from 'api/usePricing';
import { useProduct } from 'api/useProduct';
import useSections from 'api/useSections';
import useTabs from 'api/useTabs';
import { MIN_USD_PRICE } from 'constants/constants';
import {
  EFeatureFlag,
  EProductPriority,
  ESaleDiscountDisplayType,
  ESectionOfferType,
  OfferType
} from 'constants/enums';
import Decimal from 'decimal.js';
import isEmpty from 'lodash/isEmpty';
import { isPriceLocalized as isPriceLocalizedUtil } from 'utils/isPriceLocalized';

import AutoCompleteInput from 'components/AutoCompleteInput/AutoCompleteInput';
import Badge from 'components/Badge/Badge';
import BarPointsForm from 'components/BarPointsForm/BarPointsForm';
import Button from 'components/Button/Button';
import Checkbox from 'components/Checkbox/Checkbox';
import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import { Product } from 'components/ProductsForm/ProductsForm.types';
import RichText from 'components/RichText/RichText';
import Textarea from 'components/Textarea/Textarea';
import { TabsItem } from 'components/Theme/StoreTheme/storeTheme.types';
import Typography from 'components/Typography/Typography';
import { Separator } from 'components/ui/separator';

import { ProductProps } from '../../Popups/types';
import ProductsTable from '../components/ProductsTable';
import {
  LOCALIZED_PRICE_ERROR,
  PRODUCT_PRIORITY_OPTIONS,
  SALE_DISCOUNT_TYPE_OPTIONS
} from '../constants';
import { useProductManagement } from '../hooks/useProductManagement';
import {
  getBadgeLabel,
  getBadgePlaceholder,
  shouldRenderBadge
} from '../utils';

const SettingsTab = (props: any) => {
  const {
    values,
    setCurrentOfferUI,
    chosenSegment,
    setChosenSegment,
    hasFeatureFlagTags,
    formikProps,
    specialOfferId,
    products,
    setProducts,
    edit,
    relevantBadgeType,
    badgeList,
    setDirtyProducts,
    setStartHour,
    offerDesignItems,
    dup,
    setIsProgressBarFormValid,
    setDirtyProgressBar,
    setProgressBarFormValues,
    progressBarFormValues
  } = props;

  const {
    handleChange,
    handleBlur,
    touched,
    errors,
    setFieldValue,
    setTouched,
    validateField,
    validateForm
  } = formikProps;
  const { publisherId } = useParams();

  const { featureFlags } = useFeatureFlags({ publisherId });

  const hasScheduleOfferFeatureFlag =
    featureFlags?.[EFeatureFlag.DASHBOARD_SCHEDULED_OFFERS];
  const [isNameExists, setIsNameExists] = useState(false);
  const [segmentError, setSegmentError] = useState('');
  const [tagsError, setTagsError] = useState('');
  const [inputValue, setInputValue] = useState('');

  const { getPricings } = usePricing();
  const priceTypingDebounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const [isPriceLocalized, setIsPriceLocalized] = useState(false);
  if (!getPricings.data && !getPricings.isFetching) {
    getPricings.refetch();
  }
  const { getTabs } = useTabs(publisherId);
  const { getSectionsByOfferType: getSections } = useSections(
    publisherId,
    ESectionOfferType.PROMOTION
  );

  const inputRef = useRef<HTMLInputElement | null>(null);

  const { getOffers, formatProductQuantity } = useOffers({
    offerId: specialOfferId,
    offerType: OfferType.SPECIAL_OFFER
  });
  const { data: productsData, isLoading: isLoadingProducts } =
    useProduct(publisherId).getProducts;

  const {
    currentProduct,
    setCurrentProduct,
    handleQuantityChange,
    handlePriorityChange,
    removeProduct,
    editProduct,
    addProduct
  } = useProductManagement({
    products,
    setProducts,
    setDirtyProducts,
    productsData,
    formatProductQuantity,
    touched,
    setTouched,
    handleChange
  });

  const productsList: Product[] = productsData?.result;

  const enableTabs: boolean = useMemo(
    () => featureFlags?.[EFeatureFlag.STORE_TABS],
    [featureFlags]
  );

  const emptyTab = { content: 'All', key: '0', value: '0' };

  useEffect(() => {
    setIsNameExists(
      getOffers.data?.offers.find((o: any) => {
        return o.name === values.name;
      }) && !edit
    );
  }, [values.name]);

  useEffect(() => {
    setFieldValue('segments', chosenSegment);
  }, [chosenSegment]);

  // removes tag
  const removeTag = (tag: string) => {
    const filteredTags = values.tags.filter((s: string) => s !== tag);
    setFieldValue('tags', filteredTags);
  };

  const getProductsOptions = useMemo(() => {
    if (isLoadingProducts || !productsList) return [];
    const productIds = new Set(
      products.map((item: ProductProps) => item.productId)
    );
    const filteredProducts = productsList.filter(
      (product: Product) => !productIds.has(product.productId as string)
    );
    if (filteredProducts.length === 0) return [];
    return filteredProducts.map((product) => ({
      url: product.images?.[0]?.url,
      name: product.name,
      key: product.name,
      value: product.productId
    }));
  }, [productsList, products, isLoadingProducts]);

  const selectedProduct =
    getProductsOptions.find(
      (product) => product.value === currentProduct?.productId
    ) || null;

  const priceLocalizedCheckDebounce = (price: number) => {
    if (priceTypingDebounceTimeout.current) {
      clearTimeout(priceTypingDebounceTimeout.current);
    }
    priceTypingDebounceTimeout.current = setTimeout(() => {
      setIsPriceLocalized(isPriceLocalizedUtil(price, getPricings.data.result));
    }, 1000);
  };

  useEffect(() => {
    if (getPricings.data && values.price) {
      priceLocalizedCheckDebounce(
        new Decimal(values.price).times(100).toNumber()
      );
    }
  }, [getPricings.data, values.price]);

  const sectionItems = useMemo(() => {
    const items = [{ value: '0', label: 'None' }];
    if (getSections?.data?.result?.length > 0) {
      const parsedSectionsData = getSections.data.result.map(
        (section: Section) => ({
          value: section.sectionId,
          label: section.name
        })
      );
      return items.concat(parsedSectionsData);
    }
    return items;
  }, [getSections?.data?.result]);

  const getStoreTabItems = () => {
    if (!enableTabs) return [];
    let tabsData = [emptyTab];
    if (getTabs.data?.length > 0) {
      const parsedTabsData = getTabs.data.map((tab: TabsItem) => ({
        content: tab.name,
        key: tab.tabId,
        value: tab.tabId
      }));
      return tabsData.concat(parsedTabsData);
    }
    return tabsData;
  };

  const handleStoreTabChange = (value: string) => {
    if (!enableTabs) return;

    setFieldValue('tab', value);
  };

  const handleProgressBarFormChange = useCallback(
    (
      values: {
        progressBarId: string | null;
        progressBarPoints: number | null;
      },
      isValid: boolean,
      isDirty: boolean
    ) => {
      setProgressBarFormValues(values);
      setIsProgressBarFormValid(isValid);
      setDirtyProgressBar(isDirty);
    },
    [setIsProgressBarFormValid, setDirtyProgressBar, setProgressBarFormValues]
  );

  return (
    <div className="flex flex-col">
      <RichText
        title="General"
        description="Start with setting up your offer mandatory fields"
        className="mb-5"
      />
      <div className="flex flex-col gap-5 mb-2">
        <div className="grid grid-cols-2 gap-5">
          <Input
            label="Name"
            name="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            isRequired
            placeholder="Name"
            state={
              (touched.name && errors.name) || isNameExists
                ? 'error'
                : 'default'
            }
            description={
              (touched.name ? errors.name?.toString() : '') ||
              (isNameExists ? 'Name already exists' : '')
            }
          />
          <Input
            label="Display Name"
            name="displayName"
            value={values.displayName}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Display Name"
            state={
              touched.displayName && errors.displayName ? 'error' : 'default'
            }
            description={
              touched.displayName ? errors.displayName?.toString() : ''
            }
          />
        </div>
        <div>
          <Textarea
            label="Description"
            name="description"
            placeholder="Placeholder"
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </div>
        <div className="grid grid-cols-2 gap-5">
          <Input
            label="External ID"
            name="publisherOfferId"
            value={values.publisherOfferId}
            onChange={handleChange}
            onBlur={handleBlur}
            isRequired
            placeholder="ID Number"
            state={
              Boolean(errors.publisherOfferId) &&
              (touched.publisherOfferId || dup)
                ? 'error'
                : 'default'
            }
            description={
              touched.publisherOfferId || dup
                ? errors.publisherOfferId?.toString()
                : ''
            }
          />
          {!hasScheduleOfferFeatureFlag && (
            <Input
              label="Availability"
              name="playerAvailability"
              value={values.playerAvailability || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              isRequired={values.setAsFree}
              type="number"
              state={
                touched.playerAvailability && Boolean(errors.playerAvailability)
                  ? 'error'
                  : 'default'
              }
              description={
                touched.playerAvailability
                  ? errors.playerAvailability?.toString()
                  : ''
              }
            />
          )}
          <InputSelectDropdown
            label="Offer Design"
            isRequired
            placeholder="Select Offer Design"
            listDataTestId="dashPromoDesignList"
            buttonDataTestId="offerDesignInpt"
            options={
              offerDesignItems?.map((item: any) => ({
                value: item.value,
                label: item.content
              })) || []
            }
            defaultSelected={
              offerDesignItems?.find(
                (item: any) => item.value === values.offerUiId
              )
                ? {
                    value: values.offerUiId,
                    label:
                      offerDesignItems.find(
                        (item: any) => item.value === values.offerUiId
                      )?.content || ''
                  }
                : undefined
            }
            onOptionChange={(value) => {
              setCurrentOfferUI(value);
              setFieldValue('offerUiId', value);
            }}
            description={
              touched.offerUiId && errors.offerUiId
                ? errors.offerUiId?.toString()
                : ''
            }
          />
          <InputSelectDropdown
            dataTestId="promotionSectionInput"
            label="Section"
            placeholder="Select Section"
            options={sectionItems}
            defaultSelected={
              sectionItems.find((item) => item.value === values.section) ||
              undefined
            }
            onOptionChange={(value) => {
              setFieldValue('section', value);
            }}
          />
          {enableTabs && (
            <InputSelectDropdown
              label="Tab"
              placeholder="Select Tab"
              options={
                getStoreTabItems()?.map((item: any) => ({
                  value: item.value,
                  label: item.content
                })) || []
              }
              defaultSelected={
                getStoreTabItems()?.find(
                  (item: any) => item.value === values.tab
                )
                  ? {
                      value: values.tab,
                      label:
                        getStoreTabItems().find(
                          (item: any) => item.value === values.tab
                        )?.content || ''
                    }
                  : undefined
              }
              onOptionChange={handleStoreTabChange}
              dataTestId="bundleTabInput"
            />
          )}
          {values.setAsFree ? (
            <Input
              label="Price"
              name="price"
              value={values.price === 0 ? 'Free' : values.price.toString()}
              onChange={(e) => {
                setTouched({
                  ...touched,
                  price: true
                });
                handleChange(e);
                validateField('price');
              }}
              onBlur={handleBlur}
              disabled={true}
              type="text"
              state={
                touched.price && Boolean(errors.price) ? 'error' : 'default'
              }
              description={touched.price ? errors.price?.toString() : ''}
            />
          ) : (
            <Input
              label="Price"
              name="price"
              value={values.price}
              onChange={(e) => {
                setTouched({
                  ...touched,
                  price: true
                });
                handleChange(e);
                validateField('price');
              }}
              onBlur={handleBlur}
              isRequired
              type="number"
              min={MIN_USD_PRICE}
              leftIcon={<span>$</span>}
              leftIconStyles="text-base-foreground text-p-s absolute left-3 top-1/2 -translate-y-1/2"
              state={
                (touched.price && Boolean(errors.price)) || !isPriceLocalized
                  ? 'error'
                  : 'default'
              }
              description={
                (touched.price ? errors.price?.toString() : '') ||
                (isPriceLocalized ? '' : LOCALIZED_PRICE_ERROR)
              }
            />
          )}
        </div>
      </div>
      {featureFlags?.[EFeatureFlag.DASHBOARD_OFFERS_FREE] && (
        <Checkbox
          checked={values.setAsFree}
          onCheckedChange={async (checked) => {
            const isChecked = checked === true;
            await setFieldValue('setAsFree', isChecked);

            // Update related fields programmatically and validate immediately
            if (isChecked) {
              await setFieldValue('price', 0);
              await setFieldValue('playerAvailability', 1);
            } else {
              setStartHour('');
              await setFieldValue('price', MIN_USD_PRICE);
              await setFieldValue('playerAvailability', undefined);
              await setFieldValue('schedule', {
                permanent: values?.schedule.permanent,
                timeFrames: [...values?.schedule?.timeFrames]
              });
            }

            await validateForm(); // Revalidate the entire form to reflect changes
          }}
          label="Set as Free"
        />
      )}
      <Separator className="my-5 bg-base-border" />
      <RichText
        title="Segments"
        description="Create or add players segments to your offer"
        className="mt-4 mb-5"
      />
      <div className="flex flex-col gap-5">
        <div>
          <Typography className="text-black font-medium text-sm mb-2">
            Segment Name
          </Typography>
          <Input
            ref={inputRef}
            value={inputValue}
            placeholder="Search Segment"
            dataTestId="promoSegmentsInpt"
            rightIcon={
              <FontAwesomeIcon
                icon="fa-regular fa-magnifying-glass"
                className="w-4 h-4"
              />
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter' && inputValue.trim()) {
                e.preventDefault();
                setChosenSegment([...chosenSegment, inputValue.trim()]);
                setInputValue('');
                inputRef?.current?.blur();
              }
            }}
            onChange={(e) => {
              setInputValue(e.target.value);
              setSegmentError(
                chosenSegment.includes(e.target.value)
                  ? "You can't add the same segment twice"
                  : ''
              );
            }}
            onBlur={handleBlur}
            state={segmentError ? 'error' : 'default'}
            description={segmentError || ''}
          />
          {isEmpty(chosenSegment) && (
            <Typography className="text-zinc-500 text-sm mt-2">
              Press 'Enter' to add a new segment
            </Typography>
          )}
          {!isEmpty(chosenSegment) && (
            <div className="mt-3 flex flex-row flex-wrap gap-2">
              {chosenSegment.map((val: string) => (
                <Badge
                  key={val}
                  isRemovable
                  onRemove={() => {
                    setChosenSegment((oldSegments: any) => {
                      const newSegments = oldSegments.filter(
                        (existingVal: string) => existingVal !== val
                      );
                      setFieldValue('segments', newSegments);
                      return newSegments;
                    });
                  }}
                >
                  {val}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
      <Separator className="my-5 bg-base-border" />
      {shouldRenderBadge(relevantBadgeType) && (
        <>
          <RichText
            className="mt-4 mb-5"
            title="Badges"
            description='Highlight your offer with a badge like "Most Popular" or "Best Value"'
          />
          <div className="flex flex-col gap-5">
            <InputSelectDropdown
              label={getBadgeLabel(relevantBadgeType)}
              placeholder={getBadgePlaceholder(relevantBadgeType)}
              buttonDataTestId="promoBadgesInpt"
              options={
                badgeList?.map((item: any) => ({
                  value: item.value,
                  label: item.content
                })) || []
              }
              defaultSelected={
                badgeList?.find((item: any) => item.value === values.badge)
                  ? {
                      value: values.badge,
                      label:
                        badgeList.find(
                          (item: any) => item.value === values.badge
                        )?.content || ''
                    }
                  : undefined
              }
              onOptionChange={(value) => setFieldValue('badge', value)}
            />
          </div>
          <Separator className="my-5 bg-base-border" />
        </>
      )}
      <RichText
        className="mt-4 mb-9"
        title="Sale & Discounts"
        description="Enhance your offer by providing greater value at a better price."
      />
      <div
        className={`flex flex-col gap-5 ${values.price > 0 ? 'mb-4' : 'mb-0'}`}
      >
        <div className="grid grid-cols-2 gap-5">
          <Input
            label="Min Product Sale"
            name="salePercentage"
            value={values.salePercentage}
            onChange={handleChange}
            onBlur={handleBlur}
            type="number"
            min={0}
            step={0.1}
            leftIcon={
              values?.salePercentageDisplayType ===
              ESaleDiscountDisplayType.PERCENTAGE ? (
                <span>%</span>
              ) : undefined
            }
            leftIconStyles="text-base-foreground text-p-s absolute left-2 top-1/2 -translate-y-1/2"
            state={
              touched.salePercentage && errors.salePercentage
                ? 'error'
                : 'default'
            }
            description={
              touched.salePercentage ? errors.salePercentage?.toString() : ''
            }
          />
          <InputSelectDropdown
            label="Type"
            placeholder="Select Type"
            options={SALE_DISCOUNT_TYPE_OPTIONS}
            defaultSelected={{
              value:
                values?.salePercentageDisplayType ||
                ESaleDiscountDisplayType.PERCENTAGE,
              label:
                values?.salePercentageDisplayType ===
                ESaleDiscountDisplayType.PERCENTAGE
                  ? 'Percentage'
                  : values?.salePercentageDisplayType ===
                      ESaleDiscountDisplayType.MULTIPLIER
                    ? 'Multiplier'
                    : 'Fixed amount'
            }}
            onOptionChange={(value) =>
              setFieldValue('salePercentageDisplayType', value)
            }
            buttonDataTestId="productSaleTypeDropDown"
          />
        </div>
        <div>
          {values.price > 0 && (
            <>
              <RichText
                className="mt-4 mb-5"
                title="Advanced Pricing & Discounts"
              />
              <div className="grid grid-cols-3 gap-5">
                <Input
                  label="Crossed price"
                  name="priceBeforeDiscount"
                  value={values.priceBeforeDiscount}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  type="number"
                  min={0}
                  step={0.1}
                  leftIcon={<span>$</span>}
                  leftIconStyles="text-base-foreground text-p-s absolute left-3 top-1/2 -translate-y-1/2"
                  state={
                    touched.priceBeforeDiscount &&
                    Boolean(errors.priceBeforeDiscount)
                      ? 'error'
                      : 'default'
                  }
                  description={
                    touched.priceBeforeDiscount
                      ? errors.priceBeforeDiscount?.toString()
                      : ''
                  }
                />
                <Input
                  label="Price Discount"
                  name="discount"
                  value={values.discount}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  type="number"
                  min={0}
                  step={0.1}
                  leftIcon={
                    values?.priceDiscountDisplayType ===
                    ESaleDiscountDisplayType.PERCENTAGE ? (
                      <span>%</span>
                    ) : undefined
                  }
                  leftIconStyles="text-base-foreground text-p-s absolute left-2 top-1/2 -translate-y-1/2"
                  state={
                    touched.discount && errors.discount ? 'error' : 'default'
                  }
                  description={
                    touched.discount ? errors.discount?.toString() : ''
                  }
                />
                <InputSelectDropdown
                  label="Type"
                  placeholder="Select Type"
                  disabled
                  options={SALE_DISCOUNT_TYPE_OPTIONS}
                  defaultSelected={{
                    value:
                      values?.priceDiscountDisplayType ||
                      ESaleDiscountDisplayType.PERCENTAGE,
                    label:
                      values?.priceDiscountDisplayType ===
                      ESaleDiscountDisplayType.PERCENTAGE
                        ? 'Percentage'
                        : values?.priceDiscountDisplayType ===
                            ESaleDiscountDisplayType.MULTIPLIER
                          ? 'Multiplier'
                          : 'Fixed amount'
                  }}
                  onOptionChange={(value) =>
                    setFieldValue('priceDiscountDisplayType', value)
                  }
                  buttonDataTestId="priceDiscountTypeDropDown"
                />
              </div>
            </>
          )}
        </div>
      </div>
      <Separator className="my-9 bg-base-border" />
      <RichText
        className="mb-5"
        title="Progress Bar"
        description="Track and update progress for your products"
      />
      <BarPointsForm
        initialValues={{
          progressBarId: progressBarFormValues.progressBarId,
          progressBarPoints: progressBarFormValues.progressBarPoints
        }}
        onChange={handleProgressBarFormChange}
      />
      <Separator className="my-5 bg-base-border" />
      <RichText
        className="mt-4 mb-5"
        title="Products"
        description="Add products to your offer"
      />
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-2 gap-5">
          <AutoCompleteInput
            label="Product name"
            options={getProductsOptions}
            placeholder="Search product"
            onChange={(newValue: any) => {
              setCurrentProduct({
                ...currentProduct,
                productId: newValue?.value || '',
                quantity: currentProduct?.quantity || 0,
                priority: currentProduct?.priority || EProductPriority.SUB
              });
            }}
            currentOption={selectedProduct}
            dataTestId="dashPromoProductList"
          />
          <InputSelectDropdown
            label="Products Type"
            placeholder="Select Priority"
            options={PRODUCT_PRIORITY_OPTIONS}
            listDataTestId="promoMainSubInpt"
            defaultSelected={{
              value: currentProduct?.priority || EProductPriority.SUB,
              label: currentProduct?.priority || EProductPriority.SUB
            }}
            onOptionChange={handlePriorityChange}
          />
          <Input
            label="Quantity"
            name="quantity"
            value={currentProduct?.quantity}
            type="number"
            min={0}
            onChange={(e) => handleQuantityChange(e)}
            state={touched.quantity && errors.quantity ? 'error' : 'default'}
            description={touched.quantity ? errors.quantity?.toString() : ''}
          />
        </div>
        <Button
          variant="secondary"
          disabled={!currentProduct?.productId || !currentProduct?.quantity}
          onClick={addProduct}
          leftIcon={
            <FontAwesomeIcon
              icon="fa-regular fa-circle-plus"
              className="w-5 h-5"
            />
          }
          className="w-full"
        >
          Add Product
        </Button>
        <ProductsTable
          products={products}
          onEdit={(product) => editProduct(product._id)}
          onDelete={(product) => removeProduct(product._id)}
        />
      </div>
      {hasFeatureFlagTags && (
        <>
          <Separator className="my-5 bg-base-border" />
          <RichText
            title="Tags"
            description="Create or add tags to your offer"
            className="mt-4 mb-5"
          />
          <div className="flex flex-col gap-5 mb-9">
            <div>
              <Typography className="text-black font-medium text-sm mb-2">
                Tag Name
              </Typography>
              <Input
                ref={inputRef}
                value={inputValue}
                placeholder="Search Tag"
                rightIcon={
                  <FontAwesomeIcon
                    icon="fa-regular fa-magnifying-glass"
                    className="w-4 h-4"
                  />
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && inputValue.trim()) {
                    e.preventDefault();
                    setFieldValue('tags', [...values.tags, inputValue.trim()]);
                    setInputValue('');
                    inputRef?.current?.blur();
                  }
                }}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  setTagsError(
                    values.tags.includes(e.target.value)
                      ? "You can't add the same tag twice"
                      : ''
                  );
                }}
                onBlur={handleBlur}
                state={tagsError ? 'error' : 'default'}
                description={tagsError || ''}
              />
              <Typography className="text-zinc-500 text-sm mt-2">
                Press 'Enter' to add a new tag
              </Typography>
              {!!values.tags.length && (
                <div className="mt-3 flex flex-row flex-wrap gap-2">
                  {values.tags.map((val: string) => (
                    <Badge
                      key={val}
                      isRemovable
                      onRemove={() => removeTag(val)}
                    >
                      {val}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SettingsTab;
